import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdSave } from 'react-icons/md';

interface UserFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    type: 'buyer' | 'seller';
    role?: 'admin' | '';
    company?: string;
    website?: string;
    address?: string;
    vatNumber?: string;
    siren?: string;
    siret?: string;
    isValidated?: boolean;
    isKYCVerified?: boolean;
    canSellWithGSheet?: boolean;
    blockedSeller?: boolean;
}

export default function UserForm() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState<UserFormData>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        type: 'buyer',
        role: '',
        company: '',
        website: '',
        address: '',
        vatNumber: '',
        siren: '',
        siret: '',
        isValidated: false,
        isKYCVerified: false,
        canSellWithGSheet: false,
        blockedSeller: false,
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchUser = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/users/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const user = response.data;
            setFormData({
                firstName: user.name || '',
                lastName: user.surname || '',
                email: user.email || '',
                phone: user.phone || '',
                type: user.type || 'buyer',
                role: user.role || '',
                company: user.company || '',
                website: user.website || '',
                address: user.address || '',
                vatNumber: user.vatNumber || '',
                siren: user.siren || '',
                siret: user.siret || '',
                isValidated: user.isValidated || false,
                isKYCVerified: user.isKYCVerified || false,
                canSellWithGSheet: user.canSellWithGSheet || false,
                blockedSeller: user.blockedSeller || false,
            });
        } catch (error: any) {
            console.error('Error fetching user:', error);
            setError(error.response?.data?.message || 'Failed to load user');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.email.trim()) {
            setError('Email is required');
            return;
        }

        try {
            setSaving(true);
            setError('');
            const token = localStorage.getItem('token');
            
            await axios.patch(`${CONFIG.apiUrl}/admin/users/${id}`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            navigate('/back-office/users');
        } catch (error: any) {
            console.error('Error saving user:', error);
            setError(error.response?.data?.message || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field: keyof UserFormData, value: any) => {
        setFormData({ ...formData, [field]: value });
    };

    if (loading) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                </Card>
            </div>
        );
    }

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/back-office/users')}
                        className="rounded-lg bg-gray-200 p-2 text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        Edit User
                    </div>
                </div>

                {error && (
                    <div className="mb-4 rounded-lg bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Basic Information
                        </h3>
                        
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>
                        </div>
                    </div>

                    {/* User Type & Role */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            User Type & Role
                        </h3>
                        
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Type *
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => handleChange('type', e.target.value as 'buyer' | 'seller')}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                    required
                                >
                                    <option value="buyer">Buyer</option>
                                    <option value="seller">Seller</option>
                                </select>
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Role
                                </label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => handleChange('role', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                >
                                    <option value="">Regular User</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Company Information */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Company Information
                        </h3>
                        
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Company
                                </label>
                                <input
                                    type="text"
                                    value={formData.company}
                                    onChange={(e) => handleChange('company', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    value={formData.website}
                                    onChange={(e) => handleChange('website', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Address
                                </label>
                                <input
                                    type="text"
                                    value={formData.address}
                                    onChange={(e) => handleChange('address', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    VAT Number
                                </label>
                                <input
                                    type="text"
                                    value={formData.vatNumber}
                                    onChange={(e) => handleChange('vatNumber', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    SIREN
                                </label>
                                <input
                                    type="text"
                                    value={formData.siren}
                                    onChange={(e) => handleChange('siren', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    SIRET
                                </label>
                                <input
                                    type="text"
                                    value={formData.siret}
                                    onChange={(e) => handleChange('siret', e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Verification Status */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Verification Status
                        </h3>

                        <div className="space-y-3">
                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={formData.isValidated}
                                    onChange={(e) => handleChange('isValidated', e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Account Validated
                                </span>
                            </label>

                            <label className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={formData.isKYCVerified}
                                    onChange={(e) => handleChange('isKYCVerified', e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    KYC Verified
                                </span>
                            </label>

                            {formData.type === 'seller' && (
                                <div>
                                    <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Niveau vendeur
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, blockedSeller: true, canSellWithGSheet: false })}
                                            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                formData.blockedSeller
                                                    ? 'bg-red-500 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                            }`}
                                        >
                                            Vendeur -
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, blockedSeller: false, canSellWithGSheet: false })}
                                            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                !formData.blockedSeller && !formData.canSellWithGSheet
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                            }`}
                                        >
                                            Vendeur +
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, blockedSeller: false, canSellWithGSheet: true })}
                                            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                                formData.canSellWithGSheet && !formData.blockedSeller
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                            }`}
                                        >
                                            Vendeur ++
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
                        <button
                            type="button"
                            onClick={() => navigate('/back-office/users')}
                            className="rounded-lg border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <MdSave />
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
